import { Prescripteur, useSociete, useTiersService } from '@europrocurement/l2d-domain';
import {
    AdressesAdresseJsonldAdresseCreate,
    AdressesAdresseJsonldAdresseUpdate,
    RelationSocieteTiersRelationSocieteTiersJsonldPrescripteurWriteRelationSociete,
    TiersPrescripteurJsonldPrescripteurCreate,
    TiersPrescripteurJsonldPrescripteurUpdate,
} from '@europrocurement/l2d-domain/openApi/ApiTiers';
import {
    ADD_TIERS_CTX,
    Tiers,
    TiersAddress,
    tiersApiToStateTransformer,
    useTiersCtxDispatch,
} from '@europrocurement/l2d-modules/providers/TiersProvider';
import { Step1Data, Step2Data, Step3Data } from '../providers/PrescripteurFormContext';

export type ContactCreateData = {
    civilite: number;
    nomContact: string;
    prenomContact: string;
    email: string;
    telephone: string;
};

const buildAddressPayload = (
    adresse: TiersAddress,
): AdressesAdresseJsonldAdresseCreate & AdressesAdresseJsonldAdresseUpdate => ({
    adresse1: adresse.adresse1,
    adresse2: adresse.adresse2 || undefined,
    adresse3: adresse.adresse3 || undefined,
    adresse4: adresse.adresse4 || undefined,
    codePostal: adresse.codePostal,
    ville: adresse.ville,
    codePays: adresse.codePays ?? 'FR',
    defaut: true,
});

const usePrescriberCRM = () => {
    const { prescribersApi, adressesApi } = useTiersService();
    const xIdSociete = useSociete();
    const tiersDispatch = useTiersCtxDispatch();

    const syncTiers = async (tiers: Tiers): Promise<Tiers> => {
        const currentRelation = tiers.relationsSocietes.find((r) => r.societeId === xIdSociete);

        await Promise.all([
            prescribersApi.updatePrescripteurTiersItem({
                id: String(tiers.id),
                tiersPrescripteurJsonldPrescripteurUpdate: {
                    raisonSociale: tiers.raisonSociale,
                    siren: tiers.siren,
                    tvaIntracommunautaire: tiers.tvaIntracommunautaire,
                    regimeTva: tiers.regimeTva,
                    demo: tiers.demo,
                    // personnePhysique is required by the API but absent from the Tiers context;
                    // prescripteurs are always personnes morales in this domain
                    personnePhysique: false,
                } as TiersPrescripteurJsonldPrescripteurUpdate,
                xIdSociete,
            }),
            tiers.adresse.id
                ? adressesApi.updateAdresseAdressesItem({
                      id: String(tiers.adresse.id),
                      adressesAdresseJsonldAdresseUpdate: buildAddressPayload(tiers.adresse),
                      xIdSociete,
                  })
                : adressesApi.createPrescripteurAdresseAdressesCollection({
                      tiers: tiers.id,
                      adressesAdresseJsonldAdresseCreate: buildAddressPayload(tiers.adresse),
                      xIdSociete,
                  }),
            prescribersApi.updateRelationSocietePrescripteurRelationSocieteTiersItem({
                tiers: tiers.id,
                societe: String(xIdSociete),
                relationSocieteTiersRelationSocieteTiersJsonldPrescripteurWriteRelationSociete: {
                    codeClient: tiers.codeComptable,
                    clientSuivi: currentRelation?.clientSuivi,
                    clientXl: currentRelation?.clientXl,
                    tiersNonFiable: currentRelation?.tiersNonFiable,
                    dafc: currentRelation?.dafc,
                    delaiPaiement: currentRelation?.delaiPaiement,
                    devisDetaille: currentRelation?.devisDetaille,
                    modePaiement: currentRelation?.modePaiement,
                    portefeuille: currentRelation?.portefeuille,
                    categorieSocioProfessionnelle: currentRelation?.categorieSocioProfessionnelle,
                    emailSousclientObligatoire: currentRelation?.emailSousclientObligatoire,
                    logoFormalite: currentRelation?.logoFormalite,
                    partenaireFormalite: currentRelation?.partenaireFormalite,
                } as RelationSocieteTiersRelationSocieteTiersJsonldPrescripteurWriteRelationSociete,
                xIdSociete,
            }),
        ]);

        // Re-fetch to get consistent state with all updates (tiers fields + address)
        const refreshResponse = await prescribersApi.getTiersTiersItem({
            id: String(tiers.id),
            xIdSociete,
        });

        const updated = tiersApiToStateTransformer(
            refreshResponse.data as Prescripteur,
            xIdSociete,
        );

        if (tiersDispatch) {
            tiersDispatch({ type: ADD_TIERS_CTX, payload: updated });
        }

        return updated;
    };

    const buildCreatePayload = (step1: Step1Data, step2: Step2Data, step3: Step3Data) => {
        const personnePhysique = step1.personneType === 'physique';
        const raisonSociale = personnePhysique
            ? `${step1.nom} ${step1.prenom}`.trim()
            : step1.denomination || '';

        return {
            raisonSociale,
            ...(step1.siren && { siren: step1.siren }),
            ...(step1.siret && { siret: step1.siret }),
            ...(step1.tvaIntracommunautaire && {
                tvaIntracommunautaire: step1.tvaIntracommunautaire,
            }),
            personnePhysique,
            clientSuivi: true,
            venteIndirecte: false,
            adresses: [
                {
                    adresse1: step2.rue,
                    ...(step2.adresseComplement && { adresse2: step2.adresseComplement }),
                    codePostal: step2.codePostal,
                    ville: step2.ville,
                    codePays: step2.pays,
                },
            ],
            contacts: step3.contacts.map((c) => ({
                civilite: c.civilite?.id,
                nomContact: c.nomContact,
                prenomContact: c.prenomContact,
                telephonesContact: c.telephone ? [{ telephone: c.telephone }] : [],
                mobilesContact: [],
                faxsContact: [],
                emailsContact: c.email ? [{ email: c.email }] : [],
            })),
            origine: {},
            relationsSocietes: [
                {
                    societe: { id: xIdSociete },
                    delaiPaiement: 1,
                    ...(step2.portefeuille?.id && {
                        portefeuille: { id: step2.portefeuille.id },
                    }),
                    ...(step2.csp?.id && {
                        categorieSocioProfessionnelle: { id: step2.csp.id },
                    }),
                },
            ],
        };
    };

    const createPrescripteur = async (
        step1: Step1Data,
        step2: Step2Data,
        step3: Step3Data,
    ): Promise<number | undefined> => {
        const payload = buildCreatePayload(step1, step2, step3);
        const response = await prescribersApi.createPrescripteurTiersCollection({
            tiersPrescripteurJsonldPrescripteurCreate:
                payload as TiersPrescripteurJsonldPrescripteurCreate,
            xIdSociete,
        });
        return response.data?.id;
    };

    return { syncTiers, createPrescripteur };
};

export default usePrescriberCRM;
